import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, CheckCircle2, Clock, XCircle, Trash2, Plus } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMandates() {
  const { token, user } = useAuth();
  const { primaryColor } = useTheme();
  const navigate = useNavigate();
  const [mandates, setMandates] = useState([]);
  const [clients, setClients] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAnalyst, setFilterAnalyst] = useState('all');
  const [filterOffice, setFilterOffice] = useState('all');
  const [createDialog, setCreateDialog] = useState(false);
  const [createClientId, setCreateClientId] = useState('');

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    try {
      const [mdtRes, cliRes, analRes, offRes] = await Promise.all([
        axios.get(`${API}/mandates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/analysts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/offices`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setMandates(mdtRes.data || []);
      setClients(cliRes.data || []);
      setAnalysts(analRes.data || []);
      setOffices(Array.isArray(offRes.data) ? offRes.data : []);
    } catch {} finally { setLoading(false); }
  };

  const getName = (id) => { const c = clients.find(cl => cl.user_id === id || cl.id === id); const u = c?.user || c; return u ? `${u.first_name||''} ${u.last_name||''}`.trim() : id?.slice(0,8); };
  const getAnalystId = (cid) => clients.find(cl => cl.user_id === cid || cl.id === cid)?.assigned_analyst_id || '';
  const getOfficeId = (aid) => analysts.find(a => a.id === aid)?.office_id || '';

  const filtered = mandates.filter(m => {
    if (filterAnalyst !== 'all' && getAnalystId(m.client_id) !== filterAnalyst) return false;
    if (filterOffice !== 'all' && getOfficeId(getAnalystId(m.client_id)) !== filterOffice) return false;
    return true;
  });

  const signed = filtered.filter(m => m.status === 'signed');
  const pending = filtered.filter(m => m.status === 'pending_signature');
  const active = filtered.filter(m => m.status === 'active');
  const other = filtered.filter(m => ['cancelled'].includes(m.status));

  const handleCreate = async () => {
    if (!createClientId) { toast.error('Client requis'); return; }
    try {
      await axios.post(`${API}/mandates`, { client_id: createClientId }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Mandat de gestion créé'); setCreateDialog(false); setCreateClientId(''); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleApprove = async (id) => {
    try { await axios.put(`${API}/mandates/${id}`, { action: 'approve' }, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Mandat validé'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce mandat ?')) return;
    try { await axios.delete(`${API}/mandates/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Supprimé'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const sc = { pending_signature: { l: 'En attente signature', c: 'bg-yellow-500/20 text-yellow-400', i: Clock }, signed: { l: 'Signée — À valider', c: 'bg-blue-500/20 text-blue-400', i: FileText }, active: { l: 'Actif', c: 'bg-emerald-500/20 text-emerald-400', i: CheckCircle2 }, cancelled: { l: 'Annulé', c: 'bg-red-500/20 text-red-400', i: XCircle } };

  const Row = ({ m }) => { const s = sc[m.status] || {}; const I = s.i || Clock; return (
    <Card className="bg-card/80 border-border/30">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <Badge className={`${s.c} text-xs`}><I className="w-3 h-3 mr-1" />{s.l}</Badge>
              <span className="text-xs text-muted-foreground">Ref: MDG-{m.id?.slice(0,8).toUpperCase()}</span>
            </div>
            <button onClick={() => navigate(`/admin/clients/${m.client_id}`)} className="font-semibold hover:underline">{getName(m.client_id)}</button>
            <p className="text-xs text-muted-foreground">Créé le {m.date}</p>
          </div>
          <div className="flex gap-2">
            {m.status === 'signed' && <Button size="sm" onClick={() => handleApprove(m.id)} style={{ backgroundColor: primaryColor }}>Valider</Button>}
            <Button size="sm" variant="outline" onClick={() => window.open(`${API}/mandates/${m.id}/contract?token=${token}`, '_blank')}>PDF</Button>
            <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(m.id)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardContent>
    </Card>
  ); };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="spinner" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-mandates-page">
        <div className="flex items-center justify-between">
          <div><h1 className="text-2xl font-bold">Mandats de Gestion</h1><p className="text-sm text-muted-foreground">{filtered.length} mandat(s) · {signed.length} à valider</p></div>
          <Button onClick={() => { setCreateClientId(''); setCreateDialog(true); }} style={{ backgroundColor: primaryColor }}><Plus className="w-4 h-4 mr-2" />Nouveau mandat</Button>
        </div>

        <div className="flex flex-wrap gap-3">
          {user?.role !== 'bureau' && user?.role !== 'analyst' && offices.length > 0 && (
            <Select value={filterOffice} onValueChange={setFilterOffice}><SelectTrigger className="w-48"><SelectValue placeholder="Bureau" /></SelectTrigger><SelectContent><SelectItem value="all">Tous les bureaux</SelectItem>{offices.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
          )}
          {user?.role !== 'analyst' && analysts.length > 0 && (
            <Select value={filterAnalyst} onValueChange={setFilterAnalyst}><SelectTrigger className="w-48"><SelectValue placeholder="Analyste" /></SelectTrigger><SelectContent><SelectItem value="all">Tous les analystes</SelectItem>{analysts.map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}</SelectContent></Select>
          )}
        </div>

        {signed.length > 0 && <div className="space-y-3"><h2 className="text-sm font-semibold uppercase tracking-wider text-blue-400">À valider ({signed.length})</h2>{signed.map(m => <Row key={m.id} m={m} />)}</div>}
        {pending.length > 0 && <div className="space-y-3"><h2 className="text-sm font-semibold uppercase tracking-wider text-yellow-400">En attente signature ({pending.length})</h2>{pending.map(m => <Row key={m.id} m={m} />)}</div>}
        {active.length > 0 && <div className="space-y-3"><h2 className="text-sm font-semibold uppercase tracking-wider text-emerald-400">Actifs ({active.length})</h2>{active.map(m => <Row key={m.id} m={m} />)}</div>}
        {other.length > 0 && <div className="space-y-3"><h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Historique ({other.length})</h2>{other.map(m => <Row key={m.id} m={m} />)}</div>}
        {filtered.length === 0 && <Card><CardContent className="py-12 text-center text-muted-foreground">Aucun mandat de gestion</CardContent></Card>}

        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Nouveau mandat de gestion</DialogTitle></DialogHeader>
            <div><Label>Client *</Label><Select value={createClientId} onValueChange={setCreateClientId}><SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger><SelectContent className="max-h-60">{clients.map(c => { const u = c.user||c; return <SelectItem key={c.user_id||c.id} value={c.user_id||c.id}>{u.first_name} {u.last_name}</SelectItem>; })}</SelectContent></Select></div>
            <DialogFooter><Button variant="outline" onClick={() => setCreateDialog(false)}>Annuler</Button><Button onClick={handleCreate} style={{ backgroundColor: primaryColor }}>Créer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
